import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Tag, TrendingUp, BookOpen, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { CoverImage } from "@/components/CoverImage";
import { ReportButton } from "@/components/ReportButton";
import { TagFollowButton } from "@/components/TagFollowButton";
import { useAuth } from "@/hooks/useAuth";
import { MetaTags } from "@/components/MetaTags";
import { cn } from "@/lib/utils";

interface TagWithCount {
  tag_name: string;
  book_count: number;
  user_count: number;
}

interface BookWithTag {
  id: string;
  title: string;
  authors: string[];
  cover_url?: string;
  cover_cache_url?: string;
  average_rating?: number;
  rating_count?: number;
}

export default function TagDiscovery() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [selectedTag, setSelectedTag] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [category, setCategory] = useState<string>("all");

  // Fetch popular tags
  const { data: popularTags = [], isLoading: loadingTags } = useQuery({
    queryKey: ["popular-tags"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("book_tags")
        .select("tag_name, book_id, user_id");

      if (error) throw error;

      // Count books and users per tag
      const tagStats = data.reduce((acc, { tag_name, book_id, user_id }) => {
        if (!acc[tag_name]) {
          acc[tag_name] = { books: new Set(), users: new Set() };
        }
        acc[tag_name].books.add(book_id);
        acc[tag_name].users.add(user_id);
        return acc;
      }, {} as Record<string, { books: Set<string>; users: Set<string> }>);

      return Object.entries(tagStats)
        .map(([tag_name, stats]) => ({
          tag_name,
          book_count: stats.books.size,
          user_count: stats.users.size,
        }))
        .sort((a, b) => b.book_count - a.book_count);
    },
  });

  // Fetch preset tags
  const { data: presetTags = [] } = useQuery({
    queryKey: ["preset-tags-discovery"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("preset_tags")
        .select("tag_name, category")
        .order("sort_order");

      if (error) throw error;
      return data;
    },
  });

  // Fetch books for selected tag
  const { data: taggedBooks = [], isLoading: loadingBooks } = useQuery({
    queryKey: ["tagged-books", selectedTag],
    queryFn: async () => {
      if (!selectedTag) return [];

      const { data, error } = await supabase
        .from("book_tags")
        .select(
          `
          book_id,
          books (
            id,
            title,
            authors,
            cover_url,
            cover_cache_url,
            average_rating,
            rating_count
          )
        `
        )
        .eq("tag_name", selectedTag);

      if (error) throw error;

      // Get unique books
      const uniqueBooks = new Map();
      data.forEach((item: any) => {
        if (item.books && !uniqueBooks.has(item.books.id)) {
          uniqueBooks.set(item.books.id, item.books);
        }
      });

      return Array.from(uniqueBooks.values());
    },
    enabled: !!selectedTag,
  });

  // Filter tags by search and category
  const filteredTags = popularTags.filter((tag) => {
    const matchesSearch = tag.tag_name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory =
      category === "all" ||
      presetTags.some((p) => p.tag_name === tag.tag_name && p.category === category);
    return matchesSearch && matchesCategory;
  });

  const categories = [
    { value: "all", label: "All Tags" },
    { value: "mood", label: "Moods" },
    { value: "theme", label: "Themes" },
    { value: "pace", label: "Pace" },
    { value: "representation", label: "Representation" },
    { value: "content", label: "Content" },
  ];

  return (
    <>
      <MetaTags
        title="Browse by Tags"
        description="Discover books by exploring community tags. Find your next read based on moods, themes, and reader experiences."
      />

      <div className="container mx-auto px-4 py-8 max-w-7xl w-full overflow-x-hidden">
        <div className="mb-8">
          <Button
            variant="ghost"
            onClick={() => (selectedTag ? setSelectedTag(null) : navigate(-1))}
            className="mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {selectedTag ? "Back to Tags" : "Back"}
          </Button>

          <div className="flex items-center gap-3 mb-2">
            <Tag className="w-8 h-8 text-primary" />
            <h1 className="text-4xl font-heading font-bold">
              {selectedTag ? `Books tagged: ${selectedTag}` : "Browse by Tags"}
            </h1>
          </div>
          <p className="text-muted-foreground">
            {selectedTag
              ? `Discover books tagged by the community`
              : "Explore books through community-created tags"}
          </p>
        </div>

        {!selectedTag ? (
          <div className="space-y-6">
            {/* Search and filters */}
            <Card>
              <CardContent className="p-6 space-y-4">
                <Input
                  placeholder="Search tags..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="max-w-md"
                />

                <Tabs value={category} onValueChange={setCategory}>
                  <TabsList className="flex-wrap h-auto">
                    {categories.map((cat) => (
                      <TabsTrigger key={cat.value} value={cat.value}>
                        {cat.label}
                      </TabsTrigger>
                    ))}
                  </TabsList>
                </Tabs>
              </CardContent>
            </Card>

            {/* Popular tags */}
            {loadingTags ? (
              <div className="text-center py-12">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredTags.map((tag) => (
                  <Card
                    key={tag.tag_name}
                    className="cursor-pointer hover:shadow-lg transition-shadow"
                    onClick={() => setSelectedTag(tag.tag_name)}
                  >
                    <CardHeader className="pb-3">
                      <div className="flex items-start justify-between">
                        <CardTitle className="text-xl flex items-center gap-2">
                          <TrendingUp className="w-5 h-5 text-primary" />
                          {tag.tag_name}
                        </CardTitle>
                        {user && (
                          <div className="flex items-center gap-1">
                            {tag.book_count >= 3 && (
                              <TagFollowButton
                                tagName={tag.tag_name}
                                variant="ghost"
                                size="sm"
                              />
                            )}
                            <ReportButton
                              contentType="tag"
                              contentId={tag.tag_name}
                              size="sm"
                              variant="ghost"
                            />
                          </div>
                        )}
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="flex gap-4 text-sm text-muted-foreground">
                        <div className="flex items-center gap-1">
                          <BookOpen className="w-4 h-4" />
                          <span>{tag.book_count} books</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Tag className="w-4 h-4" />
                          <span>{tag.user_count} readers</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            {!loadingTags && filteredTags.length === 0 && (
              <Card>
                <CardContent className="py-12 text-center">
                  <Tag className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">No tags found matching your search</p>
                </CardContent>
              </Card>
            )}
          </div>
        ) : (
          <div className="space-y-6">
            {/* Tag header */}
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Badge variant="default" className="text-lg px-4 py-2">
                      {selectedTag}
                    </Badge>
                    <span className="text-muted-foreground">
                      {taggedBooks.length} {taggedBooks.length === 1 ? "book" : "books"}
                    </span>
                  </div>
                  {user && (
                    <ReportButton
                      contentType="tag"
                      contentId={selectedTag}
                      variant="outline"
                    />
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Books grid */}
            {loadingBooks ? (
              <div className="text-center py-12">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                {taggedBooks.map((book: BookWithTag) => (
                  <Card
                    key={book.id}
                    className="cursor-pointer hover:shadow-lg transition-shadow overflow-hidden"
                    onClick={() => navigate(`/book/${book.id}`)}
                  >
                    <div className="aspect-[2/3] relative">
                      <CoverImage
                        bookId={book.id}
                        title={book.title}
                        author={book.authors?.[0]}
                        coverUrl={book.cover_url}
                        coverCacheUrl={book.cover_cache_url}
                        size="md"
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <CardContent className="p-3">
                      <h3 className="font-semibold text-sm line-clamp-2 mb-1">{book.title}</h3>
                      <p className="text-xs text-muted-foreground line-clamp-1">
                        {book.authors?.join(", ")}
                      </p>
                      {book.average_rating && book.rating_count && book.rating_count >= 3 && (
                        <div className="flex items-center gap-1 mt-2 text-xs">
                          <span className="text-star">★</span>
                          <span>{book.average_rating.toFixed(1)}</span>
                          <span className="text-muted-foreground">({book.rating_count})</span>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            {!loadingBooks && taggedBooks.length === 0 && (
              <Card>
                <CardContent className="py-12 text-center">
                  <BookOpen className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">No books found with this tag</p>
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </div>
    </>
  );
}
